const API_URL = process.env.REACT_APP_API_URL;
// const API_URL = 'http://192.168.168.57:7000/api';


// Get token from localStorage
const getToken = () => localStorage.getItem('customerToken');

// Generic API request handler
const apiRequest = async (endpoint, options = {}) => {
  try {
    const token = getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      headers,
      ...options,
    });

    // Check if response has content
    const text = await response.text();
    let data;
    
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      console.error('JSON Parse Error:', e, 'Response:', text);
      throw new Error('Invalid response from server');
    }

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Something went wrong');
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Product APIs
export const productAPI = {
  // Get all products for customers (public)
  getAllProducts: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.subCategory) queryParams.append('subCategory', params.subCategory);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.userCountry) queryParams.append('userCountry', params.userCountry);
    console.log(params,"params")
    const queryString = queryParams.toString();
    const endpoint = `/products/customer${queryString ? `?${queryString}` : ''}`;
    
    return apiRequest(endpoint);
  },

  // Get product by slug
  getProductBySlug: async (slug) => {
    return apiRequest(`/products/customer/${slug}`);
  },
};

// Category APIs
export const categoryAPI = {
  // Get all categories
  getAll: async () => {
    return apiRequest('/categories');
  },

  // Get all parent categories
  getAllParentCategories: async () => {
    return apiRequest('/parent-categories');
  },

  // Get all sub categories
  getAllSubCategories: async (parentCategoryId = null) => {
    const endpoint = parentCategoryId 
      ? `/sub-categories/customer?parentCategory=${parentCategoryId}`
      : '/sub-categories/customer';
    return apiRequest(endpoint);
  },

  // Alias for compatibility
  getSubCategories: async () => {
    return apiRequest('/sub-categories/customer');
  },
};

// Product Variant API
export const variantAPI = {
  getByProduct: async (productId) => {
    return apiRequest(`/product-variants/customer?product=${productId}`);
  },
};

// Auth APIs
export const authAPI = {
  sendOTP: async (phone, countryCode) => {
    return apiRequest('/customer/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ phone, countryCode }),
    });
  },

  verifyOTPAndLogin: async (phone, countryCode, otp) => {
    return apiRequest('/customer/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ phone, countryCode, otp }),
    });
  },

  getProfile: async () => {
    return apiRequest('/customer/auth/profile');
  },

  updateProfile: async (name, email, profilePic) => {
    return apiRequest('/customer/auth/profile', {
      method: 'PUT',
      body: JSON.stringify({ name, email, profilePic }),
    });
  },
};

// Cart API
export const cartAPI = {
  get: async () => {
    return apiRequest('/customer/cart');
  },

  add: async (productId, variantId, quantity = 1) => {
    return apiRequest('/customer/cart/add', {
      method: 'POST',
      body: JSON.stringify({ productId, variantId, quantity }),
    });
  },

  update: async (variantId, quantity) => {
    return apiRequest('/customer/cart/update', {
      method: 'PUT',
      body: JSON.stringify({ variantId, quantity }),
    });
  },

  remove: async (variantId) => {
    return apiRequest(`/customer/cart/remove/${variantId}`, {
      method: 'DELETE',
    });
  },

  clear: async () => {
    return apiRequest('/customer/cart/clear', {
      method: 'DELETE',
    });
  },

  sync: async (items) => {
    return apiRequest('/customer/cart/sync', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  },
};

// Wishlist APIs
export const wishlistAPI = {
  get: async () => {
    return apiRequest('/customer/wishlist');
  },

  add: async (productId) => {
    return apiRequest('/customer/wishlist/add', {
      method: 'POST',
      body: JSON.stringify({ productId }),
    });
  },

  remove: async (productId) => {
    return apiRequest(`/customer/wishlist/remove/${productId}`, {
      method: 'DELETE',
    });
  },

  clear: async () => {
    return apiRequest('/customer/wishlist/clear', {
      method: 'DELETE',
    });
  },

  check: async (productId) => {
    return apiRequest(`/customer/wishlist/check/${productId}`);
  },
};

// Order API
export const orderAPI = {
  create: async (orderData) => {
    return apiRequest('/customer/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  getAll: async () => {
    return apiRequest('/customer/orders');
  },

  getMyOrders: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = `/customer/orders${queryString ? `?${queryString}` : ''}`;
    return apiRequest(endpoint);
  },

  getById: async (orderId) => {
    return apiRequest(`/customer/orders/${orderId}`);
  },

  cancel: async (orderId) => {
    return apiRequest(`/customer/orders/${orderId}/cancel`, {
      method: 'PUT',
    });
  },

  track: async (orderNumber) => {
    return apiRequest(`/customer/orders/track/${orderNumber}`);
  },
};

// Address APIs
export const addressAPI = {
  getAll: async () => {
    return apiRequest('/customer/auth/addresses');
  },

  add: async (address) => {
    return apiRequest('/customer/auth/addresses', {
      method: 'POST',
      body: JSON.stringify(address),
    });
  },

  update: async (addressId, address) => {
    return apiRequest(`/customer/auth/addresses/${addressId}`, {
      method: 'PUT',
      body: JSON.stringify(address),
    });
  },

  delete: async (addressId) => {
    return apiRequest(`/customer/auth/addresses/${addressId}`, {
      method: 'DELETE',
    });
  },
};

// Contact API
const contactAPI = {
  sendEmail: async (contactData) => {
    return apiRequest('/contact/send', {
      method: 'POST',
      body: JSON.stringify(contactData),
    });
  },

  getStatus: async () => {
    return apiRequest('/contact/status');
  },
};

export default {
  productAPI,
  categoryAPI,
  variantAPI,
  authAPI,
  cartAPI,
  wishlistAPI,
  orderAPI,
  addressAPI,
  contactAPI,
};
